import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faThumbsUp,
  faThumbsDown,
  faArrowLeft,
  faVideo,
  faUser,
  faUniversity,
} from "@fortawesome/free-solid-svg-icons";

// Import the PNG images for section icons
import academic from "../../assets/images/Reviews/academics.png";
import club from "../../assets/images/Reviews/club.png";
import dollar from "../../assets/images/Reviews/dollar.png";
import entranceIcon from "../../assets/images/Reviews/entrance.png";
import placement from "../../assets/images/Reviews/placement.png";
import infrastructure from "../../assets/images/Reviews/club.png";
import BlogIcon from "../../assets/images/Reviews/blog.png";
import { useNavigate } from "react-router-dom";

// Section definitions with icons and questions
const sections = [
  {
    icon: entranceIcon,
    title: "Entrance Exams and Admissions",
    question:
      "Explain the admission process. You can include information regarding any/all entrance exams needed and the cut-off for the same.",
    key: "entrance_exams_and_admissions",
  },
  {
    icon: academic,
    title: "Faculty and Teaching Quality",
    question: "Describe your experience with the faculty and teaching methods.",
    key: "faculty_and_teaching_quality",
  },
  {
    icon: infrastructure,
    title: "Infrastructure and Facilities",
    question:
      "Describe the campus infrastructure, laboratories, and other facilities.",
    key: "infrastructure_and_facilities",
  },
  {
    icon: placement,
    title: "Placements and Career Support",
    question:
      "Describe the placement opportunities and career guidance provided.",
    key: "placements_and_career_support",
  },
  {
    icon: club,
    title: "Campus Life and Culture",
    question:
      "Share your experiences regarding campus life, events, and student culture.",
    key: "campus_life_and_culture",
  },
  {
    icon: dollar,
    title: "Overall Experience and Value for Money",
    question:
      "Share your overall experience and whether you think the program offers value for money.",
    key: "overall_experience_and_value_for_money",
  },
];

const BlogReviewComponent = ({ reviewData }) => {
  // Safely access all data with defaults
  const {
    name = "N/A",
    email = "N/A",
    phone_number = "N/A",
    clg_name = "N/A",
    clg_logo = "",
    year_of_admission = "N/A",
    upload_video = "",
    video_title = "Video Review",
    video_description = "",
    review_tags = "",
    blog_data = [],
  } = reviewData || {};

  const navigate = useNavigate();

  // Helper function to render star ratings
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={i < rating ? "br-star-filled" : "br-star-empty"}
        />
      );
    }
    return stars;
  };

  // Enhanced parsing function for pros/cons
  const parseList = (data) => {
    if (!data) return [];
    try {
      // Handle both stringified JSON and actual arrays
      if (typeof data === "string") {
        // Clean the string first if needed
        const cleaned = data.replace(/^\[|\]$/g, "").replace(/"/g, "");
        return cleaned
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item);
      }
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.error("Error parsing list:", e, "Data:", data);
      return [];
    }
  };

  // Render the college logo or university icon if no logo
  const renderCollegeLogo = (size = "normal") => {
    const style =
      size === "small"
        ? {
            width: "30px",
            height: "30px",
            marginRight: "10px",
            objectFit: "contain",
          }
        : {
            width: "50px",
            height: "50px",
            marginRight: "15px",
            objectFit: "contain",
          };

    if (clg_logo && typeof clg_logo === "string" && clg_logo.trim() !== "") {
      return (
        <img
          src={clg_logo}
          alt={`${clg_name} logo`}
          style={style}
          className="br-college-logo"
        />
      );
    } else {
      return (
        <div className="br-college-logo-icon" style={style}>
          <FontAwesomeIcon
            icon={faUniversity}
            size={size === "small" ? "lg" : "2x"}
          />
        </div>
      );
    }
  };

  // Render a complete blog section
  const renderBlogSection = (section) => {
    const sectionData =
      blog_data.find((item) => item.blog_review_type === section.key) || {};

    const {
      detailed_review = "",
      ratings = 0,
      pro = [],
      con = [],
    } = sectionData;

    const pros = parseList(pro);
    const cons = parseList(con);

    return (
      <div key={section.key} className="br-blog-section">
        {/* Section Header */}
        <div className="br-section-header">
          <h3 className="br-section-title">
            <span className="br-section-icon" style={{ marginRight: "10px" }}>
              <img
                src={section.icon}
                alt={section.title}
                style={{ width: "auto", height: "30px", objectFit: "contain" }}
              />
            </span>
            {section.title}
          </h3>
          <div className="br-rating">
            {renderStars(ratings)}
            <span className="br-rating-value">{ratings}.0</span>
          </div>
        </div>

        <div className="br-section-content">
          {/* Guiding Question */}
          <p className="br-section-question">{section.question}</p>

          {/* Detailed Review */}
          <div className="br-detailed-review">
            <h4>Detailed Experience:</h4>
            <p>{detailed_review || "N/A"}</p>
          </div>

          {/* Pros and Cons */}
          <div className="br-pros-cons-grid">
            <div className="br-pros-list">
              <h4>
                <FontAwesomeIcon icon={faThumbsUp} /> Pros
              </h4>
              {pros.length > 0 ? (
                <ul>
                  {pros.map((item, i) => (
                    <li key={`pro-${i}`}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>N/A</p>
              )}
            </div>

            <div className="br-cons-list">
              <h4>
                <FontAwesomeIcon icon={faThumbsDown} /> Cons
              </h4>
              {cons.length > 0 ? (
                <ul>
                  {cons.map((item, i) => (
                    <li key={`con-${i}`}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>N/A</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="br-container">
      {/* Page Header */}
      <div className="br-header">
        <button className="br-back-button" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="srtIcon">
          <img className="srt-img" src={BlogIcon} alt="short" />
          <h2 className="srv-page-title">Blog & Vlog Review</h2>
        </div>
      </div>

      {/* College and Reviewer Info */}
      <div className="br-reviewer-card">
        <div
          className="br-reviewer-header"
          style={{ display: "flex", alignItems: "center" }}
        >
          {renderCollegeLogo("lg")}
          <h3 className="br-college-name">{clg_name}</h3>
        </div>
        <div className="br-reviewer-content">
          <div className="br-reviewer-details">
            <div className="br-detail-item">
              <span className="br-detail-label">Reviewed by</span>
              <span className="br-detail-value">{name}</span>
            </div>
            <div className="br-detail-item">
              <span className="br-detail-label">Email</span>
              <span className="br-detail-value">{email}</span>
            </div>
            <div className="br-detail-item">
              <span className="br-detail-label">Phone Number</span>
              <span className="br-detail-value">{phone_number}</span>
            </div>
            <div className="br-detail-item">
              <span className="br-detail-label">Year of Admission</span>
              <span className="br-detail-value">{year_of_admission}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Video Section */}
      {upload_video && (
        <div className="br-video-section">
          <div className="br-video-header">
            <h3 className="br-video-title">
              <FontAwesomeIcon icon={faVideo} /> {video_title || "Video Review"}
            </h3>
          </div>
          <div className="br-video-content">
            <div className="br-video-player">
              <video controls src={upload_video} />
            </div>
            <h4>Video Description</h4>
            {video_description && (
              <p className="br-video-description">{video_description}</p>
            )}

            <h3>Review tags</h3>
            {review_tags && (
              <div className="br-tags">
                {review_tags.split(",").map((tag, i) => (
                  <span key={i} className="br-tag">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Blog Sections */}
      {blog_data && blog_data.length > 0 ? (
        <div className="br-blog-sections">
          {sections.map((section) => renderBlogSection(section))}
        </div>
      ) : (
        <div className="br-no-data">No blog review data available</div>
      )}
    </div>
  );
};

export default BlogReviewComponent;
